import { useState } from "react";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Home, Send, Loader2, Check } from "lucide-react";

// Landlord suggests other available homes to a well-qualified applicant who wasn't selected.
export const SuggestPropertiesDialog = ({ open, onOpenChange, application, properties = [], onSent }) => {
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const options = properties.filter(
    (p) => p.available && !p.hidden && p.id !== application?.property_id);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const send = async () => {
    if (selected.length === 0) { toast.error("Select at least one home to suggest"); return; }
    setBusy(true);
    try {
      await api.post(`/applications/${application.id}/suggest`, { property_ids: selected, note: note.trim() });
      toast.success(`Suggestion sent to ${application.renter_name}`);
      setSelected([]); setNote("");
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send suggestion");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="bg-white rounded-2xl max-w-lg" data-testid="suggest-properties-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Suggest other homes</DialogTitle>
          <DialogDescription>
            {application?.renter_name} applied for {application?.property_title}. Suggest other available homes of yours — no new application needed since they're already pre-qualified.
          </DialogDescription>
        </DialogHeader>

        {options.length === 0 ? (
          <p className="text-sm text-mute py-4">You have no other available listings to suggest right now.</p>
        ) : (
          <>
            <div className="max-h-72 overflow-y-auto space-y-2 -mx-1 px-1">
              {options.map((p) => {
                const on = selected.includes(p.id);
                return (
                  <button
                    key={p.id} type="button" data-testid={`suggest-option-${p.id}`}
                    onClick={() => toggle(p.id)}
                    className={`w-full text-left flex items-center gap-3 rounded-lg border p-2.5 transition-colors ${on ? "border-forest bg-forest/[0.04] ring-1 ring-forest/30" : "border-line hover:bg-bone"}`}
                  >
                    <img src={p.images?.[0] || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?q=80&w=200"} alt="" className="h-12 w-16 object-cover rounded-md shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink truncate">{p.title}</p>
                      <p className="text-xs text-mute truncate">{p.city}, {p.state} · {money(p.monthly_rent)}/mo</p>
                    </div>
                    <span className={`h-5 w-5 rounded-full border flex items-center justify-center shrink-0 ${on ? "bg-forest border-forest text-white" : "border-line"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
                  </button>
                );
              })}
            </div>
            <div>
              <Textarea
                data-testid="suggest-note"
                value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Optional note, e.g. 'This one just opened up and is very similar to what you applied for.'"
                className="border-line"
              />
            </div>
          </>
        )}

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-lg border border-line hover:bg-bone text-sm">Cancel</button>
          <button data-testid="send-suggestion-btn" disabled={busy || selected.length === 0} onClick={send} className="px-4 py-2 rounded-lg bg-forest text-white hover:bg-forest-hover text-sm flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send suggestion{selected.length > 0 ? ` (${selected.length})` : ""}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
